/**
 * A máquina de arcade da Sala de Descanso — o ecrã.
 *
 * O jogo chama-se Green Rush: conduz-se um C63 por uma estrada que nunca acaba,
 * a velocidade só sobe, e uma corrida dura o que durar a atenção de quem está a
 * ver — uns quarenta segundos, quase sempre menos.
 *
 * A razão de ser uma ARCADE e não um jogo para duas pessoas é a sala: numa sala
 * de descanso a graça não é jogar, é estar ali. Por isso UM joga e TODOS vêem —
 * ao vivo, a 60 fps, na chamada onde já estão a falar. Quem está a ver não
 * recebe vídeo nem estados: recebe o VOLANTE e corre o mesmo jogo no seu ecrã.
 * É por isso que se pode gritar "à esquerda!" e a pessoa ouvir a tempo.
 *
 * Três decisões, e as três estão do outro lado deste ficheiro:
 *
 *  1. O MOTOR é puro e inteiro (`@greenvoice/shared/rush.ts`). Sem `Math.random`,
 *     sem vírgula, sem `Date`: a mesma semente e os mesmos comandos dão o mesmo
 *     jogo em qualquer motor de JavaScript, hoje e daqui a cinco anos.
 *  2. O PROTOCOLO é o `rushNet.ts`: fila comutativa, ninguém simula à frente do
 *     que está confirmado, e um pacote perdido nota-se em vez de passar.
 *  3. AQUI só mora o ecrã — o canvas, o teclado e os botões.
 *
 * E há uma regra de desempenho que manda no ficheiro todo: **a corrida não passa
 * pelo `setState`**. Sessenta renders por segundo do React punham a chamada de
 * voz aos saltos. O jogo vive em `ref`s e escreve directamente no canvas e no
 * `textContent` do contador; o `useState` só serve para o que muda de vez em
 * quando — a fila, o recorde, quem está ao volante.
 */

import {
  RUSH_CAR_NPC,
  RUSH_CONE,
  RUSH_HOLE,
  RUSH_TPS,
  RUSH_VIEW,
  RUSH_W,
  type RushDir,
  type RushState as RushSim,
  rushCarBox,
  rushHitLabel,
  rushMeters,
  rushObsBox,
  rushSeed,
  rushStart,
  rushStep,
} from "@greenvoice/shared";
import { useCallback, useEffect, useRef, useState } from "react";

import { voiceEngine } from "../lib/voice";
import { useStore } from "../store/store";
import { IconClose } from "./Icons";
import {
  R_BEAT,
  type RMsg,
  type RushChange,
  type RushRoom,
  rEmpty,
  rHead,
  rLive,
  rParse,
  rPlace,
  rPrune,
  rReduce,
  rSyncMsg,
} from "./rushNet";

/** Milissegundos por tick. O motor corre a 60 Hz e o relógio é o do sistema —
 *  não o do `requestAnimationFrame`, que num monitor a 144 Hz daria outro jogo. */
const MS_TICK = 1000 / RUSH_TPS;

/** Quantos ticks se deixam recuperar num só fotograma. Sem isto, um separador em
 *  segundo plano acordava com dois mil ticks em atraso e bloqueava a página a
 *  tentar apanhá-los todos de uma vez. */
const MAX_CATCHUP = 10;

/** A pausa entre carregar em "Começar" e o primeiro tick. Arrancar no mesmo
 *  milissegundo em que se clica é arrancar antes de se estar a olhar. */
const ARRANQUE = 700;

/** Escala do canvas: o mundo tem 1000×2000, o ecrã fica com um quarto disso. */
const ESCALA = 4;

// ── Paleta do jogo ──────────────────────────────────────────

const ASFALTO = "#242a33";
const ASFALTO_ESC = "#1b2028";
const BERMA = "#161a20";
const RELVA = "#233a24";
const RISCA = "#c9d2dc";
const CREME = "#dfcdb0";
const CREME_ESC = "#b6a488";
const VERDE = "#348434";
const VERDE_CLARO = "#79a240";
const VIDRO = "#2b3540";
const LARANJA = "#e07a24";
const BURACO = "#0b0e12";
const NPC = "#8892a0";
const NPC_ESC = "#5d6673";

// ── A arte do carro ─────────────────────────────────────────

/**
 * O C63 visto de cima, já ao tamanho exacto da caixa do motor (180×360) e já
 * virado para onde se anda.
 *
 * Preparado em ficheiro e não em código: a origem tinha 727×1454 de carro útil,
 * e 727 → 180 é 0,248× — bem abaixo do meio-tamanho a partir do qual o
 * `drawImage` deita linhas fora em vez de as somar. Reduzido de uma vez, com
 * média de área em alfa pré-multiplicado.
 *
 * 180×360 é também o TECTO do que alguma vez se vê: o canvas nunca passa de
 * 1000×2000 px reais e a escala do contexto é `cv.width / RUSH_W`, logo no
 * melhor dos casos 1:1. Um ficheiro maior era peso para nunca se usar.
 *
 * O `v2` no nome é o que faz a subida de 150×300 chegar a quem já cá esteve: o
 * ficheiro mudou de tamanho, e uma cache a servir o `v1` daria um carro de 150
 * esticado para 180 — não desalinhado (a caixa vem do motor), mas borrado, e
 * sem nada no ecrã a dizer porquê. Nome novo, pedido novo.
 */
const CARRO_SRC = "/campus/rush/car-c63-v2.png";

/** Uma só descarga por aba, partilhada por todas as montagens do painel — a
 *  arcade abre e fecha muitas vezes numa call. */
let carroImg: HTMLImageElement | null = null;

/**
 * Pede a arte. Chamar as vezes que for: só a primeira faz alguma coisa.
 *
 * Não há flag de "pronta" a acompanhar isto de propósito. Uma flag e a imagem
 * são duas verdades sobre a mesma coisa, e é assim que se acaba a desenhar uma
 * imagem ainda por descodificar. `naturalWidth` já é a resposta, vem do DOM, e
 * continua a ser 0 depois de um 404 — que é exactamente o caso em que o plano B
 * tem de entrar.
 */
function pedeCarro(): void {
  if (carroImg || typeof Image === "undefined") return;
  const img = new Image();
  img.src = CARRO_SRC;
  carroImg = img;
}

/** A arte, ou `null` enquanto não estiver mesmo pronta a desenhar. */
function arteDoCarro(): HTMLImageElement | null {
  return carroImg && carroImg.naturalWidth > 0 ? carroImg : null;
}

/** O estado do motor local, fora do React de propósito. */
interface Motor {
  /** `corrida|piloto` — muda de valor e a simulação recomeça do zero. */
  chave: string;
  s: RushSim;
  tick: number;
  /** Cursor sobre as mudanças de volante já aplicadas. */
  i: number;
  d: RushDir;
  /** Distância percorrida, só para as riscas da estrada andarem. */
  dist: number;
}

function motorNovo(chave: string, seed: number): Motor {
  return { chave, s: rushStart(seed), tick: 0, i: 0, d: 0, dist: 0 };
}

/**
 * Avança o motor até ao tick `ate`, aplicando as mudanças de volante pelo
 * caminho. É o mesmo passeio com cursor do `rSim` — aqui incremental, porque
 * refazer a corrida do princípio a cada fotograma era refazer até 18 000 passos
 * sessenta vezes por segundo.
 */
function avanca(m: Motor, changes: readonly RushChange[], ate: number): void {
  let restam = MAX_CATCHUP;
  while (m.tick < ate && !m.s.over && restam > 0) {
    const t = m.tick + 1;
    while (m.i < changes.length && (changes[m.i]?.t ?? Number.POSITIVE_INFINITY) <= t) {
      m.d = changes[m.i]?.d ?? m.d;
      m.i += 1;
    }
    m.s = rushStep(m.s, m.d);
    m.dist += m.s.vel;
    m.tick = t;
    restam -= 1;
  }
}

// ── O desenho ───────────────────────────────────────────────

/**
 * O carro do jogador, vista de cima.
 *
 * Plano A é a arte; plano B é o desenho que aqui estava — a carrinha da Green
 * Village com a mobile home atrelada. O plano B não é decoração defensiva: sem
 * ele, um 404 no PNG deixava a estrada a andar com NADA a conduzir, e o jogo
 * ficava por jogar sem uma única mensagem de erro. Enquanto a imagem não chega
 * (primeiro fotograma, cache fria) é também o plano B que se vê.
 *
 * A arte cobre exactamente a caixa que vem do motor, e é o motor que manda:
 * `w`/`h` chegam do `rushCarBox`, nunca das dimensões do ficheiro. Um PNG
 * trocado por outro de outro tamanho encolhe ou estica — nunca desalinha do que
 * bate.
 */
function carro(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  const arte = arteDoCarro();
  if (arte) {
    ctx.drawImage(arte, x, y, w, h);
    return;
  }

  const cabW = w * 0.78;
  const cabX = x + (w - cabW) / 2;
  const cabH = h * 0.3;
  const atrH = h - cabH - 4;

  // Atrelado (à frente, porque se anda para cima).
  ctx.fillStyle = CREME;
  ctx.fillRect(x, y, w, atrH);
  ctx.fillStyle = VERDE;
  ctx.fillRect(x, y, w, 6);
  ctx.fillRect(x, y + atrH - 6, w, 6);
  ctx.fillStyle = CREME_ESC;
  ctx.fillRect(x, y + 8, 4, atrH - 16);
  ctx.fillRect(x + w - 4, y + 8, 4, atrH - 16);
  ctx.fillStyle = VERDE_CLARO;
  ctx.fillRect(x + 6, y + atrH * 0.42, w - 12, 3);

  // Lança de reboque.
  ctx.fillStyle = "#3a3f47";
  ctx.fillRect(x + w / 2 - 3, y + atrH, 6, 6);

  // Cabeça tractora.
  ctx.fillStyle = VERDE;
  ctx.fillRect(cabX, y + atrH + 4, cabW, cabH);
  ctx.fillStyle = VIDRO;
  ctx.fillRect(cabX + 5, y + atrH + 8, cabW - 10, cabH * 0.45);
  ctx.fillStyle = VERDE_CLARO;
  ctx.fillRect(cabX, y + atrH + 4, cabW, 3);

  // Rodas.
  ctx.fillStyle = "#14181e";
  for (const ry of [y + atrH * 0.25, y + atrH * 0.72, y + atrH + cabH * 0.45]) {
    ctx.fillRect(x - 3, ry, 5, 12);
    ctx.fillRect(x + w - 2, ry, 5, 12);
  }
}

/** Um obstáculo, pelo seu tipo. Cada um tem de se distinguir dos outros num
 *  relance a 44 unidades por tick — daí formas e cores sem parentesco. */
function obstaculo(ctx: CanvasRenderingContext2D, k: number, x: number, y: number, w: number, h: number): void {
  if (k === RUSH_CONE) {
    ctx.fillStyle = "#3a2a1a";
    ctx.fillRect(x, y + h - 6, w, 6);
    ctx.fillStyle = LARANJA;
    ctx.beginPath();
    ctx.moveTo(x + w / 2, y);
    ctx.lineTo(x + w, y + h - 4);
    ctx.lineTo(x, y + h - 4);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = RISCA;
    ctx.fillRect(x + w * 0.2, y + h * 0.5, w * 0.6, 5);
    return;
  }
  if (k === RUSH_HOLE) {
    ctx.fillStyle = BURACO;
    ctx.beginPath();
    ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#3b424c";
    ctx.lineWidth = 3;
    ctx.stroke();
    return;
  }
  if (k === RUSH_CAR_NPC) {
    ctx.fillStyle = NPC;
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = NPC_ESC;
    ctx.fillRect(x, y, w, 6);
    ctx.fillRect(x, y + h - 6, w, 6);
    ctx.fillStyle = VIDRO;
    ctx.fillRect(x + 6, y + h * 0.12, w - 12, h * 0.16);
    ctx.fillRect(x + 6, y + h * 0.68, w - 12, h * 0.16);
    ctx.fillStyle = "#ff5544";
    ctx.fillRect(x + 4, y + h - 10, 10, 6);
    ctx.fillRect(x + w - 14, y + h - 10, 10, 6);
    return;
  }
  // Bidão: o único que se apanha, logo o único a brilhar.
  ctx.fillStyle = VERDE_CLARO;
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = VERDE;
  ctx.fillRect(x + 4, y + 4, w - 8, h - 8);
  ctx.fillStyle = "#d6f0a8";
  ctx.fillRect(x + w * 0.3, y + h * 0.2, w * 0.4, 4);
  ctx.fillRect(x + w * 0.44, y + h * 0.2, 5, h * 0.5);
}

/** Texto centrado na estrada, com contorno para se ler por cima de tudo. */
function letreiro(ctx: CanvasRenderingContext2D, txt: string, y: number, px: number, cor: string): void {
  ctx.font = `700 ${px}px system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineWidth = Math.max(6, px * 0.16);
  ctx.strokeStyle = "rgba(6,10,14,0.92)";
  ctx.strokeText(txt, RUSH_W / 2, y);
  ctx.fillStyle = cor;
  ctx.fillText(txt, RUSH_W / 2, y);
}

/**
 * Um fotograma inteiro.
 *
 * `conta` é a contagem decrescente antes do arranque (só quem conduz a tem) e
 * `nome` quem está ao volante — os dois só existem para o letreiro.
 */
function pinta(ctx: CanvasRenderingContext2D, m: Motor, conta: number, nome: string): void {
  const { s } = m;

  ctx.fillStyle = RELVA;
  ctx.fillRect(0, 0, RUSH_W, RUSH_VIEW);
  ctx.fillStyle = ASFALTO;
  ctx.fillRect(30, 0, RUSH_W - 60, RUSH_VIEW);
  ctx.fillStyle = ASFALTO_ESC;
  ctx.fillRect(30, 0, 14, RUSH_VIEW);
  ctx.fillRect(RUSH_W - 44, 0, 14, RUSH_VIEW);
  ctx.fillStyle = BERMA;
  ctx.fillRect(20, 0, 10, RUSH_VIEW);
  ctx.fillRect(RUSH_W - 30, 0, 10, RUSH_VIEW);

  // Tracejado das faixas. O deslocamento vem da distância percorrida, por isso
  // as riscas andam à velocidade do jogo em vez de a uma cadência inventada.
  const passo = 220;
  const off = m.dist % passo;
  ctx.fillStyle = RISCA;
  for (const lx of [200, 400, 600, 800]) {
    for (let y = off - passo; y < RUSH_VIEW; y += passo) ctx.fillRect(lx - 4, y, 8, 110);
  }

  // As caixas vêm do motor — as mesmas que decidem a batida. Este ficheiro já
  // teve a sua própria conversão e desenhava o carro 150 unidades acima de
  // onde ela batia; ver `rushObsBox` no `rush.ts`.
  for (const o of s.obs) {
    const b = rushObsBox(o);
    obstaculo(ctx, o.k, b.x0, b.y0, b.w, b.h);
  }

  const c = rushCarBox(s.x);
  carro(ctx, c.x0, c.y0, c.w, c.h);

  if (s.over) {
    ctx.fillStyle = "rgba(8,12,18,0.72)";
    ctx.fillRect(0, 0, RUSH_W, RUSH_VIEW);
    letreiro(ctx, "BATESTE", RUSH_VIEW / 2 - 210, 130, "#ff6b5a");
    letreiro(ctx, rushHitLabel(s.hit), RUSH_VIEW / 2 - 90, 74, "#ffd8d2");
    letreiro(ctx, `${rushMeters(s.score)} m`, RUSH_VIEW / 2 + 60, 190, "#eaf6ee");
    if (s.cans > 0) {
      letreiro(ctx, `${s.cans} bidões apanhados`, RUSH_VIEW / 2 + 190, 66, "#8bd4a4");
    }
    letreiro(ctx, nome, RUSH_VIEW / 2 + 300, 62, "rgba(234,246,238,0.6)");
    return;
  }

  // O arranque tem uma pausa de propósito (dá tempo a pôr as mãos no teclado);
  // sem a contar, lia-se como o jogo pendurado.
  if (conta > 0) {
    ctx.fillStyle = "rgba(8,12,18,0.5)";
    ctx.fillRect(0, 0, RUSH_W, RUSH_VIEW);
    letreiro(ctx, String(conta), RUSH_VIEW / 2, 300, "#eaf6ee");
    letreiro(ctx, "← →  para desviar", RUSH_VIEW / 2 + 230, 62, "rgba(234,246,238,0.72)");
  }
}

// ── O componente ────────────────────────────────────────────

export function Arcade({
  open,
  active,
  onClose,
}: {
  /** Visível? Fechado fica MONTADO (a seguir a corrida e a responder a quem
   *  chega) mas escondido — é o que permite ir buscar um café sem sair da fila. */
  open: boolean;
  /** Ligado à call? Sem identidade local não há como entrar na fila. */
  active: boolean;
  onClose: () => void;
}) {
  const [state, setState] = useState<RushRoom>(rEmpty);

  // O callback do canal de dados é registado UMA vez — re-subscrever a cada
  // pacote perdia pacotes na troca. Logo não pode ler `state`: leria o do render
  // em que foi criado. Este espelho é a leitura sempre actual.
  const stateRef = useRef(state);
  stateRef.current = state;

  const me = active ? voiceEngine.localId : null;
  const meRef = useRef(me);
  meRef.current = me;

  const users = useStore((s) => s.users);
  const selfId = useStore((s) => s.self?.id ?? null);

  const nomeDe = useCallback(
    (id: string | null): string => {
      if (!id) return "";
      if (id === selfId) return "Tu";
      const u = users[id];
      return u ? (u.displayName ?? u.username) : "Alguém";
    },
    [users, selfId],
  );

  // Espelhos, pela mesma razão do `stateRef`: o laço dos 60 fps é montado uma
  // vez e não pode depender de nada que mude a cada render.
  const openRef = useRef(open);
  openRef.current = open;
  const nomeRef = useRef(nomeDe);
  nomeRef.current = nomeDe;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const metrosRef = useRef<HTMLSpanElement | null>(null);
  const bidoesRef = useRef<HTMLSpanElement | null>(null);

  const motorRef = useRef<Motor | null>(null);
  /** As mudanças de volante do PILOTO, escritas no momento em que acontecem. O
   *  `setState` só chega ao motor no render seguinte, e um tick de atraso na
   *  própria direcção era o jogo a responder tarde a quem lá está. */
  const meusRef = useRef<RushChange[]>([]);
  const t0Ref = useRef(0);
  const beatRef = useRef(0);
  /** Teclas em baixo. Duas ao mesmo tempo anulam-se, como um volante a sério. */
  const teclasRef = useRef({ esq: false, dir: false });
  const acabeiRef = useRef(false);

  /** Aplica localmente E envia. O reducer é determinístico, por isso aplicar já
   *  aqui não é adivinhar: é chegar ao mesmo sítio uns milissegundos antes dos
   *  outros. (O LiveKit não devolve ao remetente o que ele enviou.) */
  const enviar = useCallback((m: RMsg) => {
    setState((s) => rReduce(s, m));
    voiceEngine.sendData(m, true);
  }, []);

  // ── canal de dados ────────────────────────────────────────

  useEffect(() => {
    const off = voiceEngine.subscribeData((_from, data) => {
      const m = rParse(data);
      if (!m) return;
      if (m.k === "r:hello") {
        // Só responde quem tem alguma coisa para contar. Vários a responderem é
        // inofensivo: o `sync` mais atrasado perde no reducer.
        const s = stateRef.current;
        if (s.queue.length || rLive(s) || s.best || s.run) voiceEngine.sendData(rSyncMsg(s, m.by), true);
        return;
      }
      if (m.k === "r:sync" && m.to !== voiceEngine.localId) return;
      setState((s) => rReduce(s, m));
    });
    return off;
  }, []);

  // Ao ligar à call, pede o estado a quem já lá está.
  useEffect(() => {
    if (active) voiceEngine.sendData({ k: "r:hello", by: voiceEngine.localId ?? "" }, true);
  }, [active]);

  // Um buraco no histórico cura-se com um pedido, não com paciência.
  useEffect(() => {
    if (state.gap && active) voiceEngine.sendData({ k: "r:hello", by: voiceEngine.localId ?? "" }, true);
  }, [state.gap, active]);

  // Quem fechou o portátil sai da fila — e se estava ao volante, a corrida
  // fecha-se com o que já estava feito. A lista de presentes vem do SFU e é
  // igual em todos os ecrãs, por isso não é preciso pacote nenhum.
  useEffect(() => {
    const limpar = () => {
      const presentes = new Set(voiceEngine.getSnapshot().participants.map((p) => p.identity));
      if (presentes.size) setState((s) => rPrune(s, presentes));
    };
    limpar();
    return voiceEngine.subscribe(limpar);
  }, []);

  // ── quem é quem ───────────────────────────────────────────

  const piloto = state.pilot;
  const souPiloto = !!me && piloto === me;
  const emCorrida = rLive(state);
  const lugar = rPlace(state, me);
  const souCabeca = !emCorrida && rHead(state) === me && !!me;

  // Quando a corrida arranca comigo ao volante, começa-se um histórico limpo e
  // marca-se o instante zero. Tudo o resto (semente incluída) deduz-se.
  useEffect(() => {
    if (!souPiloto) return;
    meusRef.current = [];
    teclasRef.current = { esq: false, dir: false };
    acabeiRef.current = false;
    beatRef.current = 0;
    t0Ref.current = performance.now() + ARRANQUE;
  }, [souPiloto]);

  // ── teclado ───────────────────────────────────────────────

  useEffect(() => {
    if (!souPiloto || !open) return;
    const tecla = (e: KeyboardEvent, baixo: boolean) => {
      const k = e.key;
      if (k === "ArrowLeft" || k === "a" || k === "A") teclasRef.current.esq = baixo;
      else if (k === "ArrowRight" || k === "d" || k === "D") teclasRef.current.dir = baixo;
      else return;
      // As setas fazem a página deslizar por baixo do jogo se as deixarmos.
      e.preventDefault();
    };
    const down = (e: KeyboardEvent) => tecla(e, true);
    const up = (e: KeyboardEvent) => tecla(e, false);
    // Largar tudo ao perder o foco. Um `keydown` sem o `keyup` que lhe pertence
    // — alt-tab a meio de uma curva — deixava o volante preso para sempre: a
    // janela nunca vê a tecla a subir, e o carro voltava a encostar sozinho.
    const larga = () => {
      teclasRef.current = { esq: false, dir: false };
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", larga);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", larga);
      larga(); // e ao fechar o painel ou ao largar o volante, idem
    };
  }, [souPiloto, open]);

  // ── resolução do ecrã ─────────────────────────────────────

  // O jogo desenha-se em unidades de mundo e o CSS estica o resultado, por isso
  // a resolução REAL do canvas tem de sair da caixa que o CSS lhe deu. Fixá-la
  // num número inventado dava uma imagem borrada em qualquer ecrã denso, e as
  // riscas da estrada — linhas de 8 unidades — são exactamente o que pior
  // sobrevive a uma ampliação.
  useEffect(() => {
    // A arte pede-se aqui e não no laço: o laço corre sessenta vezes por segundo
    // e este efeito corre quando o painel abre — que é quando a arte é precisa e
    // ainda há um arranque de 700 ms para ela chegar antes do primeiro metro.
    pedeCarro();
    const cv = canvasRef.current;
    if (!cv) return;
    const medir = () => {
      const caixa = cv.getBoundingClientRect();
      if (!caixa.height) return; // painel fechado: `display:none` mede zero
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      // A altura manda e a largura sai da proporção do mundo — assim nunca há
      // esticão, mesmo que o CSS engane a caixa por um pixel de arredondamento.
      const h = Math.min(RUSH_VIEW, Math.round(caixa.height * dpr));
      const w = Math.round((h * RUSH_W) / RUSH_VIEW);
      if (cv.height !== h || cv.width !== w) {
        cv.width = w;
        cv.height = h;
      }
    };
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(cv);
    return () => ro.disconnect();
  }, [open]);

  // ── o laço dos 60 fps ─────────────────────────────────────

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;

    let vivo = true;
    let raf = 0;

    const passo = () => {
      if (!vivo) return;
      raf = requestAnimationFrame(passo);

      const sala = stateRef.current;
      const eu = meRef.current;
      const conduzo = !!eu && sala.pilot === eu;

      // Sem piloto, o motor que estiver montado FICA. É isto que deixa o ecrã
      // de fim de corrida no sítio: o `r:end` limpa o piloto e adianta o `run`,
      // e um motor novo aqui apagava a batida no fotograma seguinte ao dela.
      const chave = sala.pilot ? `${sala.run}|${sala.pilot}` : null;
      let m = motorRef.current;
      if (!m || (chave !== null && m.chave !== chave)) {
        m = motorNovo(chave ?? "", rushSeed(sala.run, rHead(sala) ?? ""));
        motorRef.current = m;
      }

      if (conduzo) {
        const agora = performance.now();
        const alvo = Math.max(0, Math.floor((agora - t0Ref.current) / MS_TICK));

        // Antes de avançar, vê-se se o volante mudou. Só quando MUDA é que há
        // pacote — é essa a economia que deixa isto correr numa call.
        if (alvo > m.tick && !m.s.over) {
          const t = m.tick + 1;
          const { esq, dir } = teclasRef.current;
          const querido: RushDir = esq === dir ? 0 : esq ? -1 : 1;
          const ultima = meusRef.current[meusRef.current.length - 1]?.d ?? 0;
          if (querido !== ultima) {
            meusRef.current.push({ t, d: querido });
            enviar({ k: "r:in", by: eu ?? "", run: sala.run, n: meusRef.current.length - 1, t, d: querido });
          }
        }

        avanca(m, meusRef.current, alvo);

        if (m.s.over && !acabeiRef.current) {
          acabeiRef.current = true;
          enviar({
            k: "r:end",
            by: eu ?? "",
            run: sala.run,
            n: meusRef.current.length,
            t: m.tick,
            m: rushMeters(m.s.score),
          });
        } else if (!m.s.over && m.tick - beatRef.current >= R_BEAT) {
          // Sinal de vida: sem ele, um piloto a segurar a direcção deixava os
          // espectadores parados a olhar, sem saber se era calma ou queda.
          beatRef.current = m.tick;
          voiceEngine.sendData({ k: "r:beat", by: eu, run: sala.run, n: meusRef.current.length, t: m.tick }, true);
        }
      } else {
        // Espectador: só se avança até onde o piloto já garantiu. Ir mais à
        // frente era adivinhar que o volante não mudou — e uma adivinhação
        // errada não se corrige, a corrida a partir dali fica outra.
        if (!sala.gap) avanca(m, sala.changes, sala.upto);
      }

      // Com o painel fechado a simulação CONTINUA (o espectador não pode perder
      // o fio da corrida) mas não se pinta nem se escreve nada: um fotograma
      // completo a 60 Hz por trás de um `display:none` era CPU roubada à call.
      if (!openRef.current) return;

      const espera = conduzo ? t0Ref.current - performance.now() : 0;
      const conta = espera > 0 ? Math.ceil(espera / (ARRANQUE / 3)) : 0;

      ctx.save();
      ctx.scale(cv.width / RUSH_W, cv.height / RUSH_VIEW);
      pinta(ctx, m, conta, nomeRef.current(sala.pilot ?? rHead(sala)));
      ctx.restore();

      // O contador escreve-se à mão. Um `setState` por fotograma punha o React
      // a redesenhar a chamada de voz sessenta vezes por segundo.
      const mt = metrosRef.current;
      if (mt) {
        const txt = `${rushMeters(m.s.score)} m`;
        if (mt.textContent !== txt) mt.textContent = txt;
      }
      const bd = bidoesRef.current;
      if (bd) {
        const txt = String(m.s.cans);
        if (bd.textContent !== txt) bd.textContent = txt;
      }
    };

    raf = requestAnimationFrame(passo);
    return () => {
      vivo = false;
      cancelAnimationFrame(raf);
    };
  }, [enviar]);

  // ── botões ────────────────────────────────────────────────

  const jogar = () => {
    if (!me || lugar > 0) return;
    // Fila vazia e máquina livre: entra-se e arranca-se de uma vez. Obrigar a
    // dois cliques quando não há mais ninguém é burocracia.
    enviar({ k: "r:join", by: me, at: state.run });
    if (!emCorrida && state.queue.length === 0) enviar({ k: "r:go", by: me, run: state.run });
  };

  const comecar = () => {
    if (!me || !souCabeca) return;
    enviar({ k: "r:go", by: me, run: state.run });
  };

  const sair = () => {
    if (!me || lugar === 0) return;
    enviar({ k: "r:quit", by: me });
  };

  /** O volante para quem joga no telemóvel. `pointer` e não `click`: um volante
   *  precisa de saber quando se LARGA, e o `click` só conta o fim. */
  const volante = (lado: "esq" | "dir", baixo: boolean) => {
    teclasRef.current[lado] = baixo;
  };

  const estado = ((): string => {
    if (!active) return "Entra na chamada para jogar.";
    if (state.gap) return "A pôr-me a par da corrida…";
    if (souPiloto) return "Setas ou A/D para desviar.";
    if (emCorrida)
      return `${nomeDe(piloto)} ao volante — ${lugar ? `és o ${lugar}.º da fila` : "podes entrar na fila"}.`;
    if (souCabeca) return "É a tua vez. Carrega em Começar.";
    if (lugar > 1) return `Estás em ${lugar}.º na fila.`;
    if (state.last) {
      const l = state.last;
      const quem = nomeDe(l.who);
      if (l.record) return `${quem} fez ${l.m} m e é o novo recorde da sala.`;
      return `${quem} fez ${l.m} m — ${rushHitLabel(l.hit)}.`;
    }
    return "Máquina livre. Uma corrida?";
  })();

  return (
    <div className={`ar${open ? "" : " ar--hidden"}`}>
      <div className="ar__panel" role="dialog" aria-label="Máquina de arcade — Green Rush">
        <header className="ar__head">
          <h2 className="ar__title">Green Rush</h2>
          <div className="ar__best">
            {state.best ? (
              <>
                <span className="ar__bestM">{state.best.m} m</span>
                <span className="ar__bestWho">{nomeDe(state.best.who)}</span>
              </>
            ) : (
              <span className="ar__bestWho">sem recorde</span>
            )}
          </div>
          <button type="button" className="ar__icon" onClick={onClose} title="Fechar a máquina">
            <IconClose size={16} />
          </button>
        </header>

        <div className="ar__body">
          <div className="ar__screen">
            <canvas
              ref={canvasRef}
              className="ar__canvas"
              width={RUSH_W / ESCALA}
              height={RUSH_VIEW / ESCALA}
              aria-label="Corrida em curso"
            />
            <div className="ar__hud">
              <span className="ar__metros" ref={metrosRef}>
                0 m
              </span>
              <span className="ar__bidoes">
                <i className="ar__bidao" aria-hidden="true" />
                <span ref={bidoesRef}>0</span>
              </span>
            </div>
            {emCorrida && !souPiloto ? (
              <span className="ar__ao-vivo">
                <i aria-hidden="true" /> {nomeDe(piloto)}
              </span>
            ) : null}
          </div>

          <aside className="ar__fila" aria-label="Fila da máquina">
            <h3 className="ar__filaTitulo">Fila</h3>
            {state.queue.length ? (
              <ol className="ar__lista">
                {state.queue.map((s, i) => (
                  <li
                    key={s.id}
                    className={`ar__lugar${s.id === piloto ? " is-pilot" : ""}${s.id === me ? " is-me" : ""}`}
                  >
                    <span className="ar__num">{i + 1}</span>
                    <span className="ar__nome">{nomeDe(s.id)}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="ar__vazia">Ninguém à espera.</p>
            )}
          </aside>
        </div>

        {souPiloto ? (
          <div className="ar__volante">
            <button
              type="button"
              className="ar__seta"
              aria-label="Desviar para a esquerda"
              onPointerDown={() => volante("esq", true)}
              onPointerUp={() => volante("esq", false)}
              onPointerLeave={() => volante("esq", false)}
              onPointerCancel={() => volante("esq", false)}
            >
              ←
            </button>
            <span className="ar__status">{estado}</span>
            <button
              type="button"
              className="ar__seta"
              aria-label="Desviar para a direita"
              onPointerDown={() => volante("dir", true)}
              onPointerUp={() => volante("dir", false)}
              onPointerLeave={() => volante("dir", false)}
              onPointerCancel={() => volante("dir", false)}
            >
              →
            </button>
          </div>
        ) : (
          <footer className="ar__foot">
            <span className="ar__status">{estado}</span>
            {souCabeca ? (
              <button type="button" className="ar__go" onClick={comecar}>
                Começar
              </button>
            ) : lugar > 0 ? (
              <button type="button" className="ar__go ar__go--sec" onClick={sair}>
                Sair da fila
              </button>
            ) : (
              <button type="button" className="ar__go" onClick={jogar} disabled={!active}>
                {emCorrida ? "Entrar na fila" : "Jogar"}
              </button>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}
